/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/scope-ag-grid-columns.ts
 * FILE_ID: QB-AGGRID-COLUMNS-001
 * PURPOSE: Convert Bravada's BASE_COLUMN_BANDS schema into AG-Grid
 *          ColGroupDef[] / ColDef[] so the new AG-Grid editor can render
 *          the same Identity / System / Measure / Labour / Rates /
 *          Products / Plant / Subcon / Outcome / Advanced / Custom band
 *          structure that the legacy grid uses.
 *
 * WHY: SAM's quote builder ships an AG-Grid editor and estimators say
 *      "you press Enter and it works". Bravada's hand-rolled grid
 *      doesn't. This module is the bridge: the new AG-Grid editor
 *      reuses the existing column-band schema instead of forking it,
 *      so schema drift between the two grids never becomes a bug.
 *
 * FLOW:
 *   ┌──────────────────────┐    ┌─────────────────┐    ┌────────────────┐
 *   │ BASE_COLUMN_BANDS +  │ -> │ buildAgGridCols │ -> │ AG-Grid        │
 *   │ visibleOptionalIds + │    │  (pure I→O)     │    │ ColGroupDef[]  │
 *   │ customColumns        │    │                 │    │                │
 *   └──────────────────────┘    └─────────────────┘    └────────────────┘
 *
 * RELATED:
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — formatters
 *   - QB-SCOPESHEET-COLUMNS-001 (scope-sheet-columns.ts) — base schema
 *   - QB-SCOPESHEET-VISIBLE-001 (visible-columns-helper.ts) — visibility
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx) — consumer
 *
 * Line-count: 300-line target is exceeded by ~2 lines after adding the
 * trade-mode gate. The builder + grouper + flattener are tightly
 * coupled to a single ColGroup tree contract; splitting them only to
 * shave 2 lines would create import churn with no real cohesion win.
 */

import {
  BASE_COLUMN_BANDS,
  applyOptionalColumnVisibility,
  buildCustomColumnBand,
} from '../scope-sheet-grid/scope-sheet-columns'
import type {
  Column,
  ColumnBand,
  CustomColumnDef,
} from '../scope-sheet-grid/ScopeSheetGridTypes'
import type { QuoteLineItem } from '@/lib/quoteApi'
import {
  CURRENCY_FIELDS,
  NUMERIC_FIELDS,
  PERCENT_FIELDS,
  fmtCurrency,
  fmtNumber,
  fmtPercent,
  getComputedValue,
} from './scope-ag-grid-formatters'
import {
  buildCheckboxColumn,
  buildActionsColumn,
} from './scope-ag-grid-action-columns'
import { decorateColDefWithPadPlaceholder } from './scope-ag-grid-pad-decorator'
import { decorateColDefWithNotesTooltip } from './scope-ag-grid-notes-decorator'
import type { AgColDef, AgColGroupDef } from './scope-ag-grid-types'
import {
  buildAllowanceBucketBand,
  DEFAULT_VISIBLE_ALLOWANCE_IDS,
} from './allowance-buckets'
import type { QuoteBuilderTradeMode } from '@/lib/trade-mode'
import {
  BRAVADA_COLUMN_BANDS,
  BRAVADA_BUILTIN_COLUMN_IDS,
} from './bravada-column-bands'

// Re-export so existing callers / tests that imported these from
// this module continue to compile after the MR-7 extraction.
export { buildCheckboxColumn, buildActionsColumn }

// ---------------------------------------------------------------------------
// Types — re-exported from scope-ag-grid-types.ts so existing imports
// (`import type { AgColDef } from './scope-ag-grid-columns'`) still work.
// ---------------------------------------------------------------------------

export type { AgColDef, AgColGroupDef } from './scope-ag-grid-types'

export interface BuildOptions {
  visibleOptionalIds?: string[]
  customColumns?: CustomColumnDef[]
  isEditable: boolean
  // Trade mode (waterproofing | steel | custom). 'waterproofing' omits
  // the steel allowance band entirely; others (incl. undefined for
  // legacy callers) keep current behaviour. Sourced from
  // Organisation.quote_builder_mode on the backend.
  tradeMode?: QuoteBuilderTradeMode
}

// ---------------------------------------------------------------------------
// Value parsers
// ---------------------------------------------------------------------------

/**
 * AG-Grid valueParser for numeric / currency columns.
 *
 * I→O. Called by AG-Grid after the user commits a cell edit and BEFORE
 * the change event reaches our `handleCellEdit` persist handler. The
 * point is to keep `data[field]` in the grid's in-memory model
 * consistent for non-formula edits (number in, number out) while
 * preserving the raw `=expression` string for formula edits so
 * `buildFormulaCellPatch` can detect + evaluate it.
 *
 * Why not strip the '=' here? Because by the time AG-Grid hands the
 * value to onCellValueChanged, we'd have already lost the source
 * expression — and we need it to persist into `QuoteLineItem.formulas`.
 *
 *   "=qty * 1.1"   → pass through as string (handler interprets)
 *   "123.45"       → 123.45 (number)
 *   "  "  / ''     → null
 *   "junk"         → null
 *
 * Note: the string-on-numeric-column window is transient (~1 React tick)
 * because the patch reaches the backend and refetches before the next
 * paint. valueFormatter renders '—' / '$NaN' for the briefly-stringified
 * value; visible only in synthetic stress tests.
 */
function parseNumericOrFormulaInput({
  newValue,
}: {
  newValue: unknown
}): string | number | null {
  if (newValue === null || newValue === undefined) return null
  if (typeof newValue === 'number') {
    return Number.isFinite(newValue) ? newValue : null
  }
  if (typeof newValue !== 'string') return null
  const trimmed = newValue.trim()
  if (trimmed === '') return null
  if (trimmed.startsWith('=')) return trimmed
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

// ---------------------------------------------------------------------------
// Column builders
// ---------------------------------------------------------------------------

/**
 * Build a single AG-Grid ColDef from a Bravada Column declaration.
 * Pure I→O. Side-effect-free.
 *
 * `tradeMode` is plumbed through so we can gate viewport-dominating
 * behaviours like Description's `flex: 1`. Steel / custom keep the
 * legacy SAM behaviour (fewer columns, description-grabs-viewport is
 * desirable). Waterproofing has 30+ Excel-spec columns laid out
 * horizontally — flexing description there hides every band past
 * Measure, which is the bug we're fixing here.
 */
export function buildAgGridColDef(
  col: Column,
  opts: { isEditable: boolean; tradeMode?: QuoteBuilderTradeMode },
): AgColDef {
  const isCustom = !!col.custom
  const isComputed = !!col.computed
  const isCurrency = isCustom
    ? col.type === 'currency'
    : CURRENCY_FIELDS.has(col.id)
  const isNumeric = isCustom
    ? col.type === 'number'
    : NUMERIC_FIELDS.has(col.id) || PERCENT_FIELDS.has(col.id)
  const isPercent = !isCustom && PERCENT_FIELDS.has(col.id)

  const baseWidth = typeof col.width === 'number' ? col.width : 150
  const colId = col.id

  const colDef: AgColDef = {
    colId,
    headerName: col.label,
    width: baseWidth,
    minWidth: 50,
    editable: opts.isEditable && !!col.editable && !isComputed,
    hide: !!col.hidden,
    cellClass: [
      col.align === 'right' ? 'ag-right-aligned-cell' : '',
      isComputed ? 'qb-computed' : '',
      col.override ? 'qb-override' : '',
    ]
      .filter(Boolean)
      .join(' '),
  }

  if (isCustom) {
    // Custom columns route reads/writes through QuoteLineItem.custom_values
    // rather than treating col.id as a real model field.
    colDef.valueGetter = ({ data }) => {
      if (!data) return null
      const cv = (data.custom_values ?? {}) as Record<string, unknown>
      const v = cv[colId]
      if (v !== undefined) return v
      return col.defaultValue ?? null
    }
    colDef.valueSetter = ({ data, newValue }) => {
      const next = { ...((data.custom_values ?? {}) as Record<string, unknown>) }
      next[colId] = newValue as string | number | boolean | null
      data.custom_values = next as QuoteLineItem['custom_values']
      return true
    }
  } else if (isComputed) {
    colDef.valueGetter = ({ data }) => getComputedValue(data, colId)
  } else {
    colDef.field = colId
  }

  if (isCurrency) {
    colDef.type = 'numericColumn'
    // Text editor (not number) so estimators can type "=qty * 1.1"
    // — the formula is detected by buildFormulaCellPatch in the
    // persist handler. Plain numbers still parse via valueParser
    // below, so non-formula edits are byte-identical to the pre-
    // formula behaviour.
    colDef.cellEditor = 'agTextCellEditor'
    colDef.valueFormatter = ({ value }) => fmtCurrency(value)
    colDef.valueParser = parseNumericOrFormulaInput
  } else if (isPercent) {
    colDef.type = 'numericColumn'
    colDef.valueFormatter = ({ value }) => fmtPercent(value)
  } else if (isNumeric) {
    colDef.type = 'numericColumn'
    colDef.cellEditor = 'agTextCellEditor'
    colDef.valueFormatter = ({ value }) => fmtNumber(value, 2)
    colDef.valueParser = parseNumericOrFormulaInput
  }

  // SAM-style placeholder ghost text on empty pad-row cells lives in
  // ./scope-ag-grid-pad-decorator.ts so this builder stays bounded.
  // The decorator no-ops on columns without a configured placeholder.
  decorateColDefWithPadPlaceholder(colDef)

  // Notes tooltip + 📝 indicator on the Description column. Composes
  // with the pad-row placeholder above (notes only fire on real rows;
  // pad rows have no notes by definition). No-op for non-description
  // columns. See QB-AGGRID-NOTES-DECORATOR-001 for the contract.
  decorateColDefWithNotesTooltip(colDef)

  // Pin the row-context columns left so the estimator always knows
  // which line they're on, even when scrolled deep into the Advanced
  // band. # + Location is ~116px pinned — enough for "1 | L1" without
  // eating too much horizontal space. Description stays UNPINNED but
  // gets flex: 1 below so it dominates the visible viewport at scroll
  // position 0 (which is where ensureColumnVisible('description') in
  // lazy-aggrid-impl puts the grid on mount).
  if (colId === 'item_number') {
    colDef.pinned = 'left'
    colDef.editable = false
  } else if (colId === 'location') {
    colDef.pinned = 'left'
  }

  // Description column flexes to fill remaining viewport space so it's
  // never cramped on a narrow screen and the prefilled defaults on pad
  // rows (unit "m2", PMPD 15) appear right next to it without the user
  // having to scroll. AG-Grid honours flex on unpinned columns; pinned
  // columns use their declared width verbatim.
  //
  // Trade-mode gate: waterproofing renders the full Excel-spec column
  // strip (30+ cols across 8 bands — see bravada-column-bands.ts). A
  // flex:1 Description there steals the entire visible viewport at
  // scroll-0, hiding LABOUR / PRODUCTS / PLANT RENTAL / SUBCONTRACTOR.
  // Estimators reading the page see "only Identity + Measure" and
  // think the new bands didn't ship. We disable the flex in
  // waterproofing mode so the declared 240px width keeps every band
  // in the page natural-scroll. Steel / custom keep current flex
  // (their schemas have far fewer cols and benefit from description
  // dominating the viewport).
  if (colId === 'description') {
    colDef.minWidth = 220
    if (opts.tradeMode !== 'waterproofing') {
      colDef.flex = 1
    }
  }

  return colDef
}

/**
 * Build an AG-Grid ColGroupDef from a Bravada column band. Returns the
 * group when the band has at least one non-hidden column / sub-band;
 * null otherwise so AG-Grid doesn't render an empty band header.
 *
 * `tradeMode` forwards down to buildAgGridColDef so per-column gating
 * (e.g. waterproofing-mode flex disable on Description) can fire.
 *
 * NESTED BAND SUPPORT: when `band.subBands` is non-empty, each sub-band
 * is recursively built as an inner ColGroupDef and prepended to the
 * children list (Excel order: sub-bands first, then aggregate columns
 * from `band.columns`). Empty sub-bands are dropped so the AG-Grid
 * header row stays tight. See the Bravada PRODUCTS band in
 * bravada-pricing-bands.ts for the only consumer today.
 */
export function buildAgGridGroupDef(
  band: ColumnBand,
  opts: { isEditable: boolean; tradeMode?: QuoteBuilderTradeMode },
): AgColGroupDef | null {
  const visibleCols = band.columns.filter((c) => !c.hidden)
  const subBands = band.subBands ?? []
  if (visibleCols.length === 0 && subBands.length === 0) return null
  const subGroups: AgColGroupDef[] = []
  for (const sub of subBands) {
    const built = buildAgGridGroupDef(sub, opts)
    if (built) subGroups.push(built)
  }
  return {
    headerName: band.label,
    groupId: `band-${band.id}`,
    marryChildren: true,
    children: [
      ...subGroups,
      ...band.columns.map((c) => buildAgGridColDef(c, opts)),
    ],
  }
}


/**
 * Top-level builder. Resolves visibility + custom columns the same way
 * the legacy ScopeSheetGrid does, then projects each band to an AG-Grid
 * column group. The AG-Grid editor renders these groups so the UI keeps
 * the Identity / System / Measure / ... / Outcome / Advanced / Custom
 * banded header that estimators are used to.
 *
 * Prepends a checkbox column and appends an actions column when
 * editable — both pinned, both outside the banded header structure.
 */
export function buildAgGridColumns(opts: BuildOptions): AgColGroupDef[] {
  // Trade-mode branch — picks the band schema that matches the org's
  // primary trade. Waterproofers get Bravada's Excel layout
  // (6 bands matching the master template @ Q10158); steel and custom
  // keep the SAM-style BASE_COLUMN_BANDS we shipped originally.
  //
  // Why a full schema swap rather than incremental column reordering:
  // estimators paste from existing Excel quotes via column position;
  // moving columns within the SAM schema would still leave Bravada
  // estimators looking at a layout that doesn't match their workflow.
  // The two schemas share the same Column / ColumnBand types so all
  // downstream code (formatters, persist handlers, formula bar) works
  // identically against either.
  const useBravadaLayout = opts.tradeMode === 'waterproofing'
  // Hide the steel allowance buckets entirely for non-steel modes
  // (carry-forward from the Tier-1 trade-mode gate). The Bravada
  // schema doesn't reference allowance ids, so this is a no-op when
  // useBravadaLayout is true.
  const hideAllowances = useBravadaLayout
  const baseBandsWithAllowances: ColumnBand[] = useBravadaLayout
    ? [...BRAVADA_COLUMN_BANDS]
    : hideAllowances
      ? [...BASE_COLUMN_BANDS]
      : [...BASE_COLUMN_BANDS, buildAllowanceBucketBand()]
  // Skip any visibleOptionalIds that the Bravada layout already
  // surfaces by default (e.g. labour_cost — first-class in Bravada
  // but optional/advanced in BASE). Without this filter we'd render
  // duplicate columns when the user previously opted-into the
  // advanced labour_cost on the SAM layout and then switched modes.
  const filteredOptionalIds = useBravadaLayout
    ? (opts.visibleOptionalIds ?? []).filter(
        (id) => !BRAVADA_BUILTIN_COLUMN_IDS.has(id),
      )
    : (opts.visibleOptionalIds ?? [])
  const effectiveVisibleIds = new Set<string>([
    ...(hideAllowances ? [] : DEFAULT_VISIBLE_ALLOWANCE_IDS),
    ...filteredOptionalIds,
  ])
  const withOptionals = applyOptionalColumnVisibility(
    baseBandsWithAllowances,
    effectiveVisibleIds,
  )
  const customBand = buildCustomColumnBand(opts.customColumns)
  const allBands = customBand ? [...withOptionals, customBand] : withOptionals
  const groups: AgColGroupDef[] = []

  // Prepend the row-selection checkbox as its own "group" containing
  // just the one un-grouped column. AG-Grid happily mixes groups and
  // plain cols at the top level. We wrap it in a group so the banded
  // header row stays flush across the entire viewport.
  if (opts.isEditable) {
    groups.push({
      headerName: '',
      groupId: 'band-select',
      marryChildren: true,
      children: [buildCheckboxColumn()],
    })
  }

  for (const band of allBands) {
    const g = buildAgGridGroupDef(band, {
      isEditable: opts.isEditable,
      tradeMode: opts.tradeMode,
    })
    if (g) groups.push(g)
  }

  // Append the per-row actions column on the right edge. Same wrap-
  // in-a-group pattern as the checkbox column above.
  if (opts.isEditable) {
    groups.push({
      headerName: '',
      groupId: 'band-actions',
      marryChildren: true,
      children: [buildActionsColumn()],
    })
  }

  return groups
}

/**
 * Flat list of editable column ids derived from a built column-group
 * tree. Used by the section editor to decide which columns participate
 * in fill-down + paste-range, and to walk the order for paste TSV.
 *
 * The synthetic `__select` and `__actions` columns are filtered out —
 * they're never editable and were never part of the paste contract.
 */
export function flattenEditableFieldIds(groups: AgColGroupDef[]): string[] {
  const out: string[] = []
  for (const group of groups) {
    for (const child of group.children) {
      if ('children' in child) {
        out.push(...flattenEditableFieldIds([child as AgColGroupDef]))
        continue
      }
      const col = child as AgColDef
      if (
        col.editable &&
        col.colId &&
        !col.hide &&
        col.colId !== '__select' &&
        col.colId !== '__actions'
      ) {
        out.push(col.colId)
      }
    }
  }
  return out
}
